<template>
  <Teleport to="body">
    <div v-if="visible" class="modal-overlay" @click.self="handleClose">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ s__('ProjectSettings|Task Templates') }}</h3>
          <button type="button" class="modal-close" @click="handleClose" aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div class="modal-body">
          <div class="info-section">
            <p>
              {{
                s__(
                  'ProjectSettings|Task templates allow project members to create reusable task structures with predefined fields, descriptions, and settings. This helps standardize task creation across the project and save time.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|How It Works') }}</h4>
            <p>
              {{
                s__(
                  'ProjectSettings|When enabled, project members can create task templates specific to the project. These templates are available to all project members and can be used to quickly create tasks with predefined structure, descriptions, priorities, and other settings.'
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Using Templates') }}</h4>
            <p>
              {{
                s__(
                  "ProjectSettings|When creating a new task, you can select a project template to pre-fill the task with the template's title, description, priority, labels, and other settings. You can then customize the task before saving."
                )
              }}
            </p>

            <h4>{{ s__('ProjectSettings|Use Cases') }}</h4>

            <div style="margin-bottom: 16px">
              <strong style="color: #333">{{
                s__('ProjectSettings|Example 1: Bug Report Template')
              }}</strong>
              <p style="margin: 4px 0 0 0; color: #666">
                {{
                  s__(
                    'ProjectSettings|Create a project template for bug reports with predefined fields like "Steps to reproduce", "Expected behavior", and "Actual behavior". This ensures all bug reports in the project follow a consistent format and include essential information.'
                  )
                }}
              </p>
            </div>

            <div style="margin-bottom: 16px">
              <strong style="color: #333">{{
                s__('ProjectSettings|Example 2: Feature Request Template')
              }}</strong>
              <p style="margin: 4px 0 0 0; color: #666">
                {{
                  s__(
                    'ProjectSettings|Set up a template for feature requests with sections for "Problem description", "Proposed solution", "Benefits", and "Acceptance criteria". This standardizes how feature requests are documented and evaluated in the project.'
                  )
                }}
              </p>
            </div>

            <div style="margin-bottom: 16px">
              <strong style="color: #333">{{
                s__('ProjectSettings|Example 3: Sprint Planning Template')
              }}</strong>
              <p style="margin: 4px 0 0 0; color: #666">
                {{
                  s__(
                    'ProjectSettings|Create a template for sprint planning tasks with predefined structure for "User story", "Acceptance criteria", "Technical notes", and "Dependencies". This helps maintain consistency in sprint planning across the project.'
                  )
                }}
              </p>
            </div>

            <h4>{{ s__('ProjectSettings|Benefits') }}</h4>
            <ul style="margin: 0 0 16px 0; padding-left: 20px; color: #666">
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Standardize task creation across the project') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Save time by reusing common task structures') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Ensure important information is not forgotten') }}
              </li>
              <li style="margin-bottom: 8px">
                {{ s__('ProjectSettings|Maintain consistency in task documentation') }}
              </li>
            </ul>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-primary" @click="handleClose">
            {{ s__('ProjectSettings|Close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { Teleport } from 'vue'
import { s__ } from '@/locale'
import CloseIcon from '../common/icons/CloseIcon.vue'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

// Lock body scroll when modal is open
useBodyScrollLock(computed(() => props.visible))

function handleClose() {
  emit('close')
}
</script>

<style scoped>
.info-section {
  line-height: 1.6;
}

.info-section h4 {
  margin: 20px 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.info-section h4:first-child {
  margin-top: 0;
}

.info-section p {
  margin: 0 0 16px 0;
  color: #666;
}

.info-section ul {
  line-height: 1.6;
}
</style>
